/**
 * Slowloris DoS Protection Service
 * Resolves CWE-400: Uncontrolled Resource Consumption
 * Implements client-side protection and server configuration guidelines
 */

class SlowlorisProtectionService {
  constructor() {
    this.requestQueue = new Map();
    this.connectionLimits = {
      maxConcurrentRequests: 15, // Increased from 10
      maxRequestsPerMinute: 100, // Increased from 60
      requestTimeout: 45000, // Increased to 45 seconds
      connectionTimeout: 15000 // Increased to 15 seconds
    };
    
    this.monitoring = {
      requestCounts: new Map(),
      suspiciousIPs: new Set(),
      blockedIPs: new Set(),
      slowConnections: new Map()
    };

    this.startMonitoring();
  }

  /**
   * Initialize request monitoring and protection
   */
  startMonitoring() {
    // Clean up old request counts every minute
    setInterval(() => {
      this.cleanupOldRequests();
    }, 60000);

    // Monitor slow connections every 30 seconds
    setInterval(() => {
      this.monitorSlowConnections();
    }, 30000);
  }

  /**
   * Protect fetch requests from Slowloris patterns
   * @param {string} url - Request URL
   * @param {Object} options - Fetch options
   * @returns {Promise} - Protected fetch promise
   */
  async protectedFetch(url, options = {}) {
    // Use smart bypass logic instead of simple authentication check
    if (this.shouldBypassProtection(url)) {

      return fetch(url, options);
    }
    
    const clientIP = await this.getClientIP();
    const requestId = this.generateRequestId();
    
    try {
      // 1. Check if IP is blocked
      if (this.monitoring.blockedIPs.has(clientIP)) {
        throw new Error('IP address is temporarily blocked due to suspicious activity');
      }

      // 2. Rate limiting check
      if (!this.checkRateLimit(clientIP)) {
        throw new Error('Rate limit exceeded. Please try again later');
      }

      // 3. Check concurrent connection limits
      if (!this.checkConcurrentConnections(clientIP)) {
        throw new Error('Too many concurrent connections from this IP');
      }

      // 4. Add request timeout protection
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => {
        timeoutController.abort();
      }, this.connectionLimits.requestTimeout);

      // 5. Combine user abort signal with timeout
      const combinedController = new AbortController();
      if (options.signal) {
        options.signal.addEventListener('abort', () => {
          combinedController.abort();
        });
      }
      timeoutController.signal.addEventListener('abort', () => {
        combinedController.abort();
      });

      // 6. Track request start
      this.trackRequestStart(requestId, clientIP);

      // 7. Enhanced fetch options
      const protectedOptions = {
        ...options,
        signal: combinedController.signal,
        headers: {
          ...options.headers
        }
      };

      // 8. Execute protected request
      const response = await fetch(url, protectedOptions);
      
      // 9. Clear timeout
      clearTimeout(timeoutId);
      
      // 10. Track successful request
      this.trackRequestComplete(requestId, clientIP, true);
      
      return response;

    } catch (error) {
      // Track failed/aborted requests
      this.trackRequestComplete(requestId, clientIP, false);
      
      // Analyze error patterns for Slowloris detection
      if (error.name === 'AbortError') {
        this.detectSlowlorisPattern(clientIP, 'timeout');
      }
      
      throw error;
    }
  }

  /**
   * Check if request should bypass protection based on user context
   * @param {string} url - Request URL
   * @returns {boolean} - True if should bypass
   */
  shouldBypassProtection(url) {
    // Always protect authentication endpoints
    if (this.isAuthenticationRequest(url)) {
      return true;
    }
    
    // For form operations, check if user is properly authenticated
    const formSavePatterns = [
      '/CreateD1', '/UpdateD1', '/CreateD2', '/UpdateD2',
      '/CreateD3', '/UpdateD3', '/CreateD4', '/UpdateD4', 
      '/CreateD5', '/UpdateD5', '/CreateD6', '/UpdateD6',
      '/CreateD7', '/UpdateD7', '/CreateD8', '/UpdateD8'
    ];
    
    if (formSavePatterns.some(pattern => url.includes(pattern))) {
      // Only bypass for authenticated users with valid session
      const userEmail = sessionStorage.getItem('userEmail');
      const userToken = sessionStorage.getItem('userToken');
      const sessionExpiry = sessionStorage.getItem('sessionExpiry');
      
      // Verify session is valid and not expired
      const isValidSession = userEmail && userToken && 
        sessionExpiry && new Date(sessionExpiry) > new Date();
        
      // Additional check for Godrej domain for internal users
      const isGodrejUser = userEmail && userEmail.toLowerCase().endsWith('@godrej.com');
      
      if (isValidSession && isGodrejUser) {

        return true;
      }
      
      // For external users, apply relaxed limits but keep protection
      if (isValidSession) {

        return false; // Keep protection but with adjusted limits
      }
    }
    
    return false;
  }
  isAuthenticationRequest(url) {
    const authPatterns = [
      '/api/UserMaster/GetUserDetailsByEmail',
      '/api/employee',
      '/oauth2/v2.0/token',
      '/oauth2/v2.0/authorize',
      'login.microsoftonline.com',
      'b2clogin.com',
      '/api/auth',
      '/api/session',
      '/api/validate',
      // Form save endpoints
      '/api/D1/CreateD1',
      '/api/D1/UpdateD1',
      '/api/D2/CreateD2',
      '/api/D2/UpdateD2',
      '/api/D3/CreateD3',
      '/api/D3/UpdateD3',
      '/api/D4/CreateD4',
      '/api/D4/UpdateD4',
      '/api/D5/CreateD5',
      '/api/D5/UpdateD5',
      '/api/D6/CreateD6',
      '/api/D6/UpdateD6',
      '/api/D7/CreateD7',
      '/api/D7/UpdateD7',
      '/api/D8/CreateD8',
      '/api/D8/UpdateD8'
    ];
    
    // Always allow authentication endpoints
    if (authPatterns.some(pattern => url.includes(pattern))) {
      return true;
    }
    
    // Special handling for UserMaster API - allow if user is authenticated
    if (url.includes('/api/UserMaster/GetUserDetailsByEmail')) {
      const userEmail = sessionStorage.getItem('userEmail');
      const userToken = sessionStorage.getItem('userToken');
      const isSupplier = sessionStorage.getItem('user_type') === 'supplier';
      
      // Allow if user has valid session (SSO/B2C authenticated)
      if (userEmail || userToken || isSupplier) {

        return true;
      }
    }
    
    return false;
  }

  /**
   * Generate unique request ID
   * @returns {string} - Unique request identifier
   */
  generateRequestId() {
    const array = new Uint8Array(8);
    crypto.getRandomValues(array);
    const hex = Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
    return `req_${Date.now()}_${hex}`;
  }

  /**
   * Get client IP address (best effort in browser)
   * @returns {Promise<string>} - Client IP or identifier
   */
  async getClientIP() {
    // In browser environment, use session-based identifier
    let clientId = sessionStorage.getItem('client_id');
    if (!clientId) {
      const array = new Uint8Array(8);
      crypto.getRandomValues(array);
      const hex = Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
      clientId = `client_${Date.now()}_${hex}`;
      sessionStorage.setItem('client_id', clientId);
    }
    return clientId;
  }

  /**
   * Get rate limits based on user context
   * @param {string} clientIP - Client identifier
   * @returns {Object} - Rate limits for this user
   */
  getUserSpecificLimits(clientIP) {
    const userEmail = sessionStorage.getItem('userEmail');
    const userType = sessionStorage.getItem('user_type');
    const isGodrejUser = userEmail && userEmail.toLowerCase().endsWith('@godrej.com');
    
    // Base limits (most restrictive)
    let limits = { ...this.connectionLimits };
    
    // Authenticated Godrej internal users - higher limits
    if (isGodrejUser && userEmail) {
      limits = {
        maxConcurrentRequests: 20,
        maxRequestsPerMinute: 120,
        requestTimeout: 60000,
        connectionTimeout: 20000
      };
    }
    // Authenticated external users - moderate limits  
    else if (userEmail && userType) {
      limits = {
        maxConcurrentRequests: 15,
        maxRequestsPerMinute: 80,
        requestTimeout: 45000,
        connectionTimeout: 15000
      };
    }
    
    return limits;
  }

  /**
   * Check rate limiting for client
   * @param {string} clientIP - Client identifier
   * @returns {boolean} - True if within rate limit
   */
  checkRateLimit(clientIP) {
    const now = Date.now();
    const windowStart = now - 60000; // 1 minute window
    
    // Get user-specific limits
    const userLimits = this.getUserSpecificLimits(clientIP);

    if (!this.monitoring.requestCounts.has(clientIP)) {
      this.monitoring.requestCounts.set(clientIP, []);
    }

    const requests = this.monitoring.requestCounts.get(clientIP);
    
    // Remove old requests outside the window
    const recentRequests = requests.filter(timestamp => timestamp > windowStart);
    this.monitoring.requestCounts.set(clientIP, recentRequests);

    // Check if within user-specific limit
    if (recentRequests.length >= userLimits.maxRequestsPerMinute) {
      this.detectSlowlorisPattern(clientIP, 'rate_limit_exceeded');
      return false;
    }

    // Add current request
    recentRequests.push(now);
    return true;
  }

  /**
   * Check concurrent connection limits
   * @param {string} clientIP - Client identifier  
   * @returns {boolean} - True if within concurrent limit
   */
  checkConcurrentConnections(clientIP) {
    const userLimits = this.getUserSpecificLimits(clientIP);
    
    const activeRequests = Array.from(this.requestQueue.values())
      .filter(req => req.clientIP === clientIP && req.active);
    
    if (activeRequests.length >= userLimits.maxConcurrentRequests) {
      this.detectSlowlorisPattern(clientIP, 'concurrent_limit_exceeded');
      return false;
    }
    
    return true;
  }

  /**
   * Track request start
   * @param {string} requestId - Request ID
   * @param {string} clientIP - Client identifier
   */
  trackRequestStart(requestId, clientIP) {
    this.requestQueue.set(requestId, {
      clientIP: clientIP,
      startTime: Date.now(),
      active: true
    });
  }

  /**
   * Track request completion
   * @param {string} requestId - Request ID
   * @param {string} clientIP - Client identifier
   * @param {boolean} success - Whether request succeeded
   */
  trackRequestComplete(requestId, clientIP, success) {
    const request = this.requestQueue.get(requestId);
    if (request) {
      request.active = false;
      request.endTime = Date.now();
      request.duration = request.endTime - request.startTime;
      request.success = success;

      // Analyze request pattern
      if (request.duration > this.connectionLimits.connectionTimeout) {
        this.detectSlowlorisPattern(clientIP, 'slow_request');
      }

      // Remove completed request after a delay
      setTimeout(() => {
        this.requestQueue.delete(requestId);
      }, 60000);
    }
  }

  /**
   * Detect Slowloris attack patterns
   * @param {string} clientIP - Client identifier
   * @param {string} pattern - Detected pattern type
   */
  detectSlowlorisPattern(clientIP, pattern) {
    console.warn(`[SlowlorisProtection] Suspicious pattern detected from ${clientIP}: ${pattern}`);
    
    // Add to suspicious IPs
    this.monitoring.suspiciousIPs.add(clientIP);
    
    // Escalate to blocking if multiple patterns detected
    const suspiciousCount = this.getSuspiciousActivityCount(clientIP);
    
    if (suspiciousCount >= 3) {
      this.blockIP(clientIP, 'Multiple Slowloris patterns detected');
    }
    
    // Log security event
    this.logSecurityEvent('slowloris_pattern_detected', {
      clientIP: clientIP,
      pattern: pattern,
      timestamp: new Date().toISOString(),
      suspiciousCount: suspiciousCount
    });
  }

  /**
   * Get suspicious activity count for IP
   * @param {string} clientIP - Client identifier
   * @returns {number} - Count of suspicious activities
   */
  getSuspiciousActivityCount(clientIP) {
    // Count various suspicious indicators
    let count = 0;
    
    if (this.monitoring.suspiciousIPs.has(clientIP)) count++;
    
    const activeConnections = Array.from(this.requestQueue.values())
      .filter(req => req.clientIP === clientIP && req.active).length;
    
    if (activeConnections > this.connectionLimits.maxConcurrentRequests / 2) count++;
    
    const recentRequests = this.monitoring.requestCounts.get(clientIP) || [];
    if (recentRequests.length > this.connectionLimits.maxRequestsPerMinute / 2) count++;
    
    return count;
  }

  /**
   * Block IP temporarily
   * @param {string} clientIP - Client identifier
   * @param {string} reason - Reason for blocking
   */
  blockIP(clientIP, reason) {
    this.monitoring.blockedIPs.add(clientIP);
    
    console.error(`[SlowlorisProtection] IP blocked: ${clientIP} - ${reason}`);
    
    // Auto-unblock after 5 minutes instead of 15
    setTimeout(() => {
      this.monitoring.blockedIPs.delete(clientIP);
      this.monitoring.suspiciousIPs.delete(clientIP);
      console.info(`[SlowlorisProtection] IP unblocked: ${clientIP}`);
    }, 5 * 60 * 1000); // Changed from 15 to 5 minutes
    
    this.logSecurityEvent('ip_blocked', {
      clientIP: clientIP,
      reason: reason,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Monitor slow connections
   */
  monitorSlowConnections() {
    const now = Date.now();
    const slowThreshold = this.connectionLimits.connectionTimeout;
    
    for (const [requestId, request] of this.requestQueue.entries()) {
      if (request.active && (now - request.startTime) > slowThreshold) {
        this.monitoring.slowConnections.set(requestId, {
          clientIP: request.clientIP,
          duration: now - request.startTime,
          timestamp: now
        });
        
        this.detectSlowlorisPattern(request.clientIP, 'persistent_slow_connection');
      }
    }
  }

  /**
   * Clean up old request data
   */
  cleanupOldRequests() {
    const now = Date.now();
    const cleanupThreshold = 5 * 60 * 1000; // 5 minutes
    
    // Clean up old request counts
    for (const [clientIP, requests] of this.monitoring.requestCounts.entries()) {
      const recentRequests = requests.filter(timestamp => 
        (now - timestamp) < cleanupThreshold
      );
      
      if (recentRequests.length === 0) {
        this.monitoring.requestCounts.delete(clientIP);
      } else {
        this.monitoring.requestCounts.set(clientIP, recentRequests);
      }
    }
    
    // Clean up old slow connections
    for (const [requestId, connection] of this.monitoring.slowConnections.entries()) {
      if ((now - connection.timestamp) > cleanupThreshold) {
        this.monitoring.slowConnections.delete(requestId);
      }
    }
  }

  /**
   * Log security events with enhanced details for VAPT compliance
   * @param {string} event - Event type
   * @param {Object} details - Event details
   */
  logSecurityEvent(event, details) {
    const logEntry = {
      event: event,
      details: details,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      // Enhanced security context
      userEmail: sessionStorage.getItem('userEmail'),
      userType: sessionStorage.getItem('user_type'),
      sessionId: sessionStorage.getItem('sessionId'),
      csrfToken: sessionStorage.getItem('client-csrf-token') ? 'present' : 'missing',
      // Security metrics
      protectionLevel: this.getProtectionLevel(),
      riskScore: this.calculateRiskScore(details)
    };
    
    // Send to server if endpoint available
    if (this.isServerEndpointAvailable()) {
      this.sendSecurityLog(logEntry);
    }
    
    // Enhanced console logging for security monitoring
    if (event.includes('blocked') || event.includes('suspicious')) {
      console.warn('[SECURITY ALERT] SlowlorisProtection:', logEntry);
    } else {
  
    }
  }

  /**
   * Check if server security endpoint is available
   * @returns {boolean} - True if server endpoint exists
   */
  isServerEndpointAvailable() {
    // This would be configured based on your backend
    return false; // Set to true when server endpoint is available
  }

  /**
   * Send security log to server
   * @param {Object} logEntry - Log entry to send
   */
  async sendSecurityLog(logEntry) {
    try {
      await fetch('/api/security/log', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(logEntry),
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
    } catch (error) {
      console.warn('[SlowlorisProtection] Failed to send security log:', error.message);
    }
  }

  /**
   * Get current protection status
   * @returns {Object} - Current protection statistics
   */
  getProtectionStatus() {
    return {
      activeConnections: this.requestQueue.size,
      suspiciousIPs: this.monitoring.suspiciousIPs.size,
      blockedIPs: this.monitoring.blockedIPs.size,
      slowConnections: this.monitoring.slowConnections.size,
      settings: this.connectionLimits,
      uptime: Date.now() - this.startTime || Date.now()
    };
  }

  /**
   * Update protection settings
   * @param {Object} newSettings - New protection settings
   */
  updateSettings(newSettings) {
    this.connectionLimits = {
      ...this.connectionLimits,
      ...newSettings
    };
    
    console.info('[SlowlorisProtection] Settings updated:', this.connectionLimits);
  }

  /**
   * Reset all protection data (admin function)
   */
  reset() {
    this.requestQueue.clear();
    this.monitoring.requestCounts.clear();
    this.monitoring.suspiciousIPs.clear();
    this.monitoring.blockedIPs.clear();
    this.monitoring.slowConnections.clear();
    
    console.info('[SlowlorisProtection] Protection data reset');
  }

  /**
   * Manually unblock a specific IP/client
   * @param {string} clientIP - Client identifier to unblock
   */
  manualUnblock(clientIP) {
    this.monitoring.blockedIPs.delete(clientIP);
    this.monitoring.suspiciousIPs.delete(clientIP);
    console.info(`[SlowlorisProtection] Manually unblocked: ${clientIP}`);
  }

  /**
   * Clear all blocks (emergency function)
   */
  clearAllBlocks() {
    this.monitoring.blockedIPs.clear();
    this.monitoring.suspiciousIPs.clear();
    console.info('[SlowlorisProtection] All blocks cleared');
  }

  /**
   * Get current protection level
   * @returns {string} - Protection level
   */
  getProtectionLevel() {
    const userEmail = sessionStorage.getItem('userEmail');
    const isGodrejUser = userEmail && userEmail.toLowerCase().endsWith('@godrej.com');
    
    if (!userEmail) return 'HIGH';
    if (isGodrejUser) return 'MEDIUM';
    return 'STANDARD';
  }

  /**
   * Calculate risk score for security events
   * @param {Object} details - Event details
   * @returns {number} - Risk score 0-100
   */
  calculateRiskScore(details) {
    let score = 0;
    
    if (details.clientIP && this.monitoring.suspiciousIPs.has(details.clientIP)) score += 30;
    if (details.pattern === 'rate_limit_exceeded') score += 25;
    if (details.pattern === 'concurrent_limit_exceeded') score += 20;
    if (details.pattern === 'slow_request') score += 15;
    if (!sessionStorage.getItem('userEmail')) score += 20;
    
    return Math.min(score, 100);
  }
}

/**
 * Enhanced fetch wrapper with Slowloris protection
 */
const slowlorisProtection = new SlowlorisProtectionService();

/**
 * Protected fetch function to replace standard fetch
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options
 * @returns {Promise} - Protected fetch promise
 */
export const protectedFetch = async (url, options = {}) => {
  // Skip Slowloris protection during SSO and B2C authentication flows
  const isAuthFlow = window.location.pathname === '/login' ||
                    window.location.pathname === '/b2c-success' ||
                    window.location.search.includes('code=') ||
                    window.location.search.includes('state=') ||
                    window.location.search.includes('error=') ||
                    sessionStorage.getItem('b2c_auth_code');
                    
  if (isAuthFlow) {

    return fetch(url, options);
  }
  
  return slowlorisProtection.protectedFetch(url, options);
};

/**
 * Get protection service instance for configuration
 */
export const getSlowlorisProtection = () => slowlorisProtection;

// Export the service instance
export { slowlorisProtection };
export default slowlorisProtection;